/**
 * Copy Trading Real API Client
 *
 * 백엔드 API와 실제 통신하는 클라이언트
 * - Axios 기반 HTTP 클라이언트
 * - JWT 토큰 자동 추가
 * - 에러 핸들링 & 타임아웃
 * - 타입 안전 (TypeScript)
 *
 * @see coin-server/apps/api/src/copy/copy.module.ts - 백엔드 엔드포인트
 */

import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  CopyPortfolio,
  CopyFollow,
  GetPortfoliosParams,
  FollowPortfolioParams,
  CreatePortfolioParams,
  UpdateFollowParams,
  OrderStatus,
} from '../../types/copyTrading';

// 환경변수에서 API URL 가져오기
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4333';
const COPY_PREFIX = import.meta.env.VITE_COPY_API_PREFIX || '/v1/copy';

/**
 * Axios 클라이언트 초기화
 */
class CopyTradingAPIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}${COPY_PREFIX}`,
      timeout: 10000, // 10초
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 요청 인터셉터: JWT 토큰 자동 추가
    this.client.interceptors.request.use(
      (config) => {
        console.log('[Copy Trading API] Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          data: config.data,
        });

        try {
          // Zustand persist 스토리지에서 토큰 가져오기
          const storageData = localStorage.getItem('token-storage');
          if (storageData) {
            const { state } = JSON.parse(storageData);
            const token = state?.accessToken;
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
              console.log('[Copy Trading API] Token added:', token.substring(0, 20) + '...');
            } else {
              console.warn('[Copy Trading API] No access token found in storage');
            }
          } else {
            console.warn('[Copy Trading API] No token-storage found in localStorage');
          }
        } catch (e) {
          console.error('[Copy Trading API] Failed to get token:', e);
        }

        console.log('[Copy Trading API] Final headers:', config.headers);
        return config;
      },
      (error) => {
        console.error('[Copy Trading API] Request interceptor error:', error);
        return Promise.reject(error);
      },
    );

    // 응답 인터셉터: 에러 핸들링
    this.client.interceptors.response.use(
      (response) => {
        console.log('[Copy Trading API] Response:', {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          data: response.data,
        });
        return response;
      },
      (error: AxiosError) => {
        // 에러 로깅
        console.error('[Copy Trading API] Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          data: error.response?.data,
          code: error.code,
        });

        // 401 Unauthorized: 토큰 만료
        if (error.response?.status === 401) {
          // 로그아웃 처리 또는 토큰 갱신
          console.warn('[Copy Trading API] Unauthorized - Token expired?');
        }

        // Network Error
        if (error.code === 'ERR_NETWORK' || !error.response) {
          console.error('[Copy Trading API] Network Error - Server may be down or CORS issue');
        }

        return Promise.reject(error);
      },
    );
  }

  // ==========================================
  // 포트폴리오 API
  // ==========================================

  /**
   * 포트폴리오 목록 조회 (공개 API)
   *
   * @param params.period - ROI/PnL 계산 기간 (24h, 7d, 30d, 90d, all)
   *   - 기본값: 'all' (누적)
   *   - '7d' 사용 시 7일간의 리더 실거래 기준 ROI/PnL 반환
   */
  async getPortfolios(
    params: GetPortfoliosParams = {},
  ): Promise<ApiResponse<PaginatedResponse<CopyPortfolio>>> {
    // 백엔드 응답은 items/pagination 형식
    // 백엔드 스키마: sortBy (roi, totalPnl 등) + period (24h, 7d, 30d, 90d, all)
    // 프론트에서 'monthlyRoi'로 요청 시 → sortBy=roi + period=30d 로 변환
    let apiSortBy: string = params.sortBy || 'totalPnl';
    let apiPeriod: string | undefined = params.period; // ⭐ 직접 period 파라미터 사용

    if (apiSortBy === 'monthlyRoi' || apiSortBy === 'roi30d') {
      apiSortBy = 'roi';
      apiPeriod = apiPeriod || '30d';
    } else if (apiSortBy === 'totalRoi' || apiSortBy === 'roiAll') {
      apiSortBy = 'roi';
      apiPeriod = apiPeriod || 'all';
    } else if (apiSortBy === 'aum') {
      apiSortBy = 'totalPnl';
    }

    const { data } = await this.client.get<ApiResponse<any>>(
      '/portfolios',
      {
        params: {
          sortBy: apiSortBy,
          order: params.order || 'desc',
          status: params.status,
          page: params.page || 1,
          limit: params.pageSize || 20,
          ...(apiPeriod && { period: apiPeriod }),
        },
      },
    );

    // 백엔드 응답을 프론트 타입으로 변환 (items → data)
    const totalCount = data.data?.pagination?.total || 0;
    const pageSize = data.data?.pagination?.limit || 20;

    return {
      success: data.success,
      data: {
        data: (data.data?.items || []).map((item: any) => this.mapPortfolioFromAPI(item, apiPeriod)),
        total: totalCount,
        page: data.data?.pagination?.page || 1,
        pageSize: pageSize,
        totalPages: Math.ceil(totalCount / pageSize),
      },
    };
  }

  /**
   * 포트폴리오 상세 조회 (공개 API)
   */
  async getPortfolioById(id: number): Promise<ApiResponse<CopyPortfolio>> {
    const { data } = await this.client.get<ApiResponse<any>>(`/portfolios/${id}`);

    return {
      success: data.success,
      data: this.mapPortfolioFromAPI(data.data),
    };
  }

  /**
   * 포트폴리오 생성 (리더 전용)
   */
  async createPortfolio(params: CreatePortfolioParams): Promise<ApiResponse<CopyPortfolio>> {
    const { data } = await this.client.post<ApiResponse<any>>('/portfolios', {
      portfolioName: params.portfolioName,
      description: params.description,
      imageUrl: params.imageUrl,
      profitShareRate: params.profitShareRate.toFixed(2), // "15.00"
      minCopyAmount: params.minCopyAmount.toFixed(8), // "100.00000000"
    });

    return {
      success: data.success,
      data: this.mapPortfolioFromAPI(data.data),
    };
  }

  // ==========================================
  // 팔로우 API
  // ==========================================

  /**
   * 팔로우 시작 (카피 시작)
   */
  async followPortfolio(params: FollowPortfolioParams): Promise<ApiResponse<CopyFollow>> {
    const { data } = await this.client.post<ApiResponse<any>>('/follows', {
      portfolioId: params.portfolioId,
      copyRatio: params.copyRatio.toFixed(4), // "0.5000"
      allocatedAmount: params.allocatedAmount.toFixed(8), // "1000.00000000"
    });

    return {
      success: data.success,
      data: this.mapFollowFromAPI(data.data),
    };
  }

  /**
   * 내 팔로우 목록 조회
   */
  async getMyFollows(params: { status?: number } = {}): Promise<
    ApiResponse<{
      follows: Array<{
        follow: CopyFollow;
        portfolio: CopyPortfolio;
        stats: any;
      }>;
      summary: {
        totalAllocated: number;
        totalPnl: number;
        totalProfitShared: number;
      };
    }>
  > {
    const { data } = await this.client.get<ApiResponse<any>>('/follows', {
      params: {
        status: params.status,
        page: 1,
        limit: 100,
      },
    });

    // 디버깅: 응답 전체 로깅
    console.log('[Copy Trading API] getMyFollows response:', JSON.stringify(data, null, 2));

    if (!data.success) {
      console.error('[Copy Trading API] API returned error:', data.error);
      return {
        success: false,
        error: data.error,
      } as any;
    }

    // 응답 데이터 검증
    if (!data.data || !data.data.items) {
      console.error('[Copy Trading API] Invalid response structure:', data);
      return {
        success: true,
        data: {
          follows: [],
          summary: {
            totalAllocated: 0,
            totalPnl: 0,
            totalProfitShared: 0,
          },
        },
      };
    }

    console.log('[Copy Trading API] Items count:', data.data.items.length);
    if (data.data.items.length > 0) {
      console.log('[Copy Trading API] First item structure:', data.data.items[0]);
    }

    // 백엔드 FollowWithPortfolio[] → 프론트 형식으로 변환
    const follows = data.data.items
      .filter((item: any) => item && typeof item === 'object') // null/undefined 필터링
      .map((item: any) => {
        // Follow 데이터 추출 (portfolio 제외)
        const { portfolio: portfolioInfo, ...followData } = item;

        // 필수 필드 검증
        if (!followData.id || !followData.copyRatio) {
          console.error('[Copy Trading API] Invalid follow data:', followData);
          return null;
        }

        return {
          follow: this.mapFollowFromAPI(followData),
          portfolio: {
            id: portfolioInfo?.id || 0,
            portfolioName: portfolioInfo?.portfolioName || '',
            leaderUserId: 0,
            leaderUsername: portfolioInfo?.leaderUsername || '',
            leaderEmail: null,
            description: '',
            imageUrl: portfolioInfo?.metadata?.imageUrl || portfolioInfo?.imageUrl || null,
            profitShareRate: 0,
            minCopyAmount: portfolioInfo?.allocatedAmount | portfolioInfo?.minCopyAmount || 0,
            currentFollowers: 0,
            maxFollowers: 0,
            totalPnl: 0,
            monthlyReturn: 0,
            totalTrades: 0,
            winRate: 0,
            avgHoldingTime: 0,
            maxDrawdown: 0,
            sharpeRatio: 0,
            riskLevel: 1,
            status: 1,
            isPublic: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as CopyPortfolio,
          stats: {
            portfolioId: portfolioInfo?.id || 0,
            totalPnl: 0,
            monthlyReturn: 0,
            totalTrades: 0,
            winRate: 0,
            avgHoldingTime: 0,
            maxDrawdown: 0,
            sharpeRatio: 0,
          },
        };
      })
      .filter((item) => item !== null); // null 제거

    // 백엔드 API 응답 형식 그대로 반환
    return {
      success: data.success,
      data: {
        follows: follows as any,
        summary: {
          totalAllocated: 0,
          totalPnl: 0,
          totalProfitShared: 0,
        },
      },
    };
  }

  /**
   * 팔로우 일시정지
   */
  async pauseFollow(followId: number): Promise<ApiResponse<CopyFollow>> {
    const { data } = await this.client.patch<ApiResponse<any>>(`/follows/${followId}/pause`);

    return {
      success: data.success,
      data: this.mapFollowFromAPI(data.data),
    };
  }

  /**
   * 팔로우 재개
   */
  async resumeFollow(followId: number): Promise<ApiResponse<CopyFollow>> {
    const { data } = await this.client.patch<ApiResponse<any>>(`/follows/${followId}/resume`);

    return {
      success: data.success,
      data: this.mapFollowFromAPI(data.data),
    };
  }

  /**
   * 팔로우 설정 업데이트
   */
  async updateFollow(
    followId: number,
    params: { copyRatio?: number; allocatedAmount?: number },
  ): Promise<ApiResponse<CopyFollow>> {
    console.log('[Copy Trading API] updateFollow called with:', { followId, params });

    const { data } = await this.client.patch<ApiResponse<any>>(`/follows/${followId}`, {
      ...(params.copyRatio !== undefined ? { copyRatio: params.copyRatio.toFixed(4) } : {}),
      ...(params.allocatedAmount !== undefined
        ? { allocatedAmount: params.allocatedAmount.toFixed(8) }
        : {}),
    });

    console.log('[Copy Trading API] updateFollow response:', data);

    if (!data.success) {
      console.error('[Copy Trading API] updateFollow failed:', data.error);
      return {
        success: false,
        data: {} as CopyFollow,
        error: data.error,
      };
    }

    console.log('[Copy Trading API] updateFollow data.data:', data.data);

    const mappedFollow = this.mapFollowFromAPI(data.data);
    console.log('[Copy Trading API] updateFollow mapped follow:', mappedFollow);

    return {
      success: true,
      data: mappedFollow,
    };
  }

  /**
   * 팔로우 종료 (언팔로우)
   */
  async unfollowPortfolio(followId: number): Promise<ApiResponse<CopyFollow>> {
    const { data } = await this.client.delete<ApiResponse<any>>(`/follows/${followId}`);

    return {
      success: data.success,
      data: this.mapFollowFromAPI(data.data),
    };
  }

  /**
   * 내 포트폴리오 조회 (리더 전용)
   */
  async getMyPortfolio(): Promise<ApiResponse<CopyPortfolio | null>> {
    const { data } = await this.client.get('/portfolios/my');
    
    if (!data.data) {
      return {
        success: true,
        data: null,
      };
    }

    return {
      success: data.success,
      data: this.mapPortfolioFromAPI(data.data),
    };
  }

  /**
   * 내 포트폴리오 수정 (리더 전용)
   */
  async updateMyPortfolio(params: Partial<CreatePortfolioParams>): Promise<ApiResponse<CopyPortfolio>> {
    const payload: any = {};
    if (params.portfolioName) payload.portfolioName = params.portfolioName;
    if (params.description !== undefined) payload.description = params.description;
    if (params.imageUrl !== undefined) payload.imageUrl = params.imageUrl;
    if (params.profitShareRate) payload.profitShareRate = params.profitShareRate.toFixed(2);
    if (params.minCopyAmount) payload.minCopyAmount = params.minCopyAmount.toFixed(8);
    if (params.status) payload.status = params.status;

    const { data } = await this.client.patch('/portfolios/my', payload);

    return {
      success: data.success,
      data: this.mapPortfolioFromAPI(data.data),
    };
  }

  // ==========================================
  // 주문/포지션/정산 API (미구현 - MVP에서 제외)
  // ==========================================

  /**
   * 내 팔로워 목록 조회 (리더 전용)
   */
  async getMyFollowers(): Promise<ApiResponse<any>> {
    const { data } = await this.client.get('/followers');
    
    return {
      success: data.success,
      data: {
        items: data.data?.items || [],
        pagination: data.data?.pagination || { total: 0, page: 1, limit: 100 },
      },
    };
  }

  async getMyCopyPositions(): Promise<ApiResponse<any>> {
    throw new Error('Not implemented in MVP');
  }

  async getMyCopyOrders(params: { page?: number; limit?: number; symbol?: string; status?: string } = {}): Promise<ApiResponse<any>> {
    const apiParams = {
      page: params.page || 1,
      limit: params.limit || 20,
      // ⭐ undefined 체크로 status=0 (Pending) 도 전달 가능
      ...(params.symbol !== undefined && params.symbol !== '' && { symbol: params.symbol }),
      ...(params.status !== undefined && params.status !== '' && { status: params.status }),
    };
    console.log('[CopyTradingAPI] getMyCopyOrders apiParams:', apiParams);

    const { data } = await this.client.get('/orders', { params: apiParams });
    console.log('[CopyTradingAPI] getMyCopyOrders response:', data);
    return data;
  }

  async getMySettlements(): Promise<ApiResponse<any>> {
    throw new Error('Not implemented in MVP');
  }

  // ==========================================
  // 데이터 변환 헬퍼 (백엔드 → 프론트)
  // ==========================================

  /**
   * 백엔드 Portfolio → 프론트 CopyPortfolio
   *
   * @param apiData - 백엔드 API 응답 데이터
   * @param period - 요청한 기간 (7d, 30d 등) - ROI 필드 매핑에 사용
   */
  private mapPortfolioFromAPI(apiData: any, period?: string): CopyPortfolio {
    console.log('[mapPortfolioFromAPI] Raw data:', apiData, 'period:', period);

    // 기간별 PnL (periodPnl이 있으면 사용, 없으면 totalPnl 사용)
    const periodPnl = apiData.periodPnl !== undefined
      ? parseFloat(apiData.periodPnl)
      : undefined;

    // 기간별 ROI (API에서 roi 필드로 반환)
    const roi = apiData.roi !== undefined ? parseFloat(apiData.roi) : undefined;

    const mapped = {
      id: apiData.id,
      leaderUserId: apiData.leaderUserId,
      leaderUsername: apiData.leaderUsername || 'Unknown',
      leaderAvatar: apiData.leaderAvatar,
      portfolioName: apiData.portfolioName,
      description: apiData.description,
      imageUrl: apiData.metadata?.imageUrl, // metadata JSON에서 추출
      profitShareRate: parseFloat(apiData.profitShareRate || '0'), // "15.00" → 15
      minCopyAmount: parseFloat(apiData.minCopyAmount || '100'), // "100.00000000" → 100
      maxFollowers: apiData.maxFollowers || 5, // 기본값 5
      currentFollowers: apiData.currentFollowers || 0,
      totalPnl: parseFloat(apiData.totalPnl || '0'), // "5000.50000000" → 5000.5
      periodPnl, // ⭐ 기간별 PnL
      roi, // ⭐ 기간별 ROI (period에 따라 7일/30일 등)
      // 기간별 ROI를 해당 필드에 매핑
      roi7d: period === '7d' ? roi : (apiData.roi7d !== undefined ? parseFloat(apiData.roi7d) : undefined),
      roi30d: period === '30d' ? roi : (apiData.roi30d !== undefined ? parseFloat(apiData.roi30d) : undefined),
      roiAll: period === 'all' ? roi : (apiData.roiAll !== undefined ? parseFloat(apiData.roiAll) : undefined),
      status: apiData.status || 1,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt,
      totalTrades: apiData.totalTrades || 0,
    };

    console.log('[mapPortfolioFromAPI] Mapped data:', {
      minCopyAmount: mapped.minCopyAmount,
      maxFollowers: mapped.maxFollowers,
      roi: mapped.roi,
      roi7d: mapped.roi7d,
      periodPnl: mapped.periodPnl,
      totalPnl: mapped.totalPnl,
    });

    return mapped;
  }

  /**
   * 백엔드 Follow → 프론트 CopyFollow
   */
  private mapFollowFromAPI(apiData: any): CopyFollow {
    // 필수 필드 검증
    if (!apiData) {
      console.error('[Copy Trading API] mapFollowFromAPI: apiData is null/undefined');
      throw new Error('Invalid follow data: apiData is null');
    }

    if (!apiData.copyRatio) {
      console.error('[Copy Trading API] mapFollowFromAPI: Missing copyRatio', apiData);
      throw new Error('Invalid follow data: copyRatio is required');
    }

    return {
      id: apiData.id,
      followerUserId: apiData.followerUserId,
      portfolioId: apiData.portfolioId,
      portfolio: apiData.portfolio ? this.mapPortfolioFromAPI(apiData.portfolio) : undefined,
      copyRatio: Number.parseFloat(apiData.copyRatio), // "0.5000" → 0.5
      allocatedAmount: Number.parseFloat(apiData.allocatedAmount || '0'), // "1000.00000000" → 1000
      currentPnl: Number.parseFloat(apiData.currentPnl || '0'), // "0.00000000" → 0
      totalProfitShared: Number.parseFloat(apiData.totalProfitShared || '0'), // MVP에서는 0
      status: apiData.status || 1,
      startedAt: apiData.startedAt,
      pausedAt: apiData.pausedAt,
      stoppedAt: apiData.stoppedAt,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt,
    };
  }
}

// Singleton 인스턴스
const apiClient = new CopyTradingAPIClient();

// ==========================================
// Export Functions (기존 Mock API와 동일한 인터페이스)
// ==========================================

export const getPortfolios = (params?: GetPortfoliosParams) => apiClient.getPortfolios(params);

export const getPortfolioById = (id: number) => apiClient.getPortfolioById(id);

export const createPortfolio = (params: CreatePortfolioParams) => apiClient.createPortfolio(params);

export const followPortfolio = (params: FollowPortfolioParams) => apiClient.followPortfolio(params);

export const getMyFollows = (params?: { status?: number }) => apiClient.getMyFollows(params);

export const pauseFollow = (followId: number) => apiClient.pauseFollow(followId);

export const resumeFollow = (followId: number) => apiClient.resumeFollow(followId);

export const updateFollow = (params: UpdateFollowParams) =>
  apiClient.updateFollow(params.followId, {
    copyRatio: params.copyRatio,
    allocatedAmount: params.allocatedAmount,
  });

export const unfollowPortfolio = (followId: number) => apiClient.unfollowPortfolio(followId);

export const getMyPortfolio = () => apiClient.getMyPortfolio();

export const updateMyPortfolio = (params: Partial<CreatePortfolioParams>) =>
  apiClient.updateMyPortfolio(params);

export const getMyFollowers = () => apiClient.getMyFollowers();

export const getMyCopyPositions = () => apiClient.getMyCopyPositions();

export const getMyCopyOrders = (params?: { page?: number; limit?: number; symbol?: string; status?: string }) => apiClient.getMyCopyOrders(params);

export const getMySettlements = () => apiClient.getMySettlements();
